/**
 * Chapter review — confirm what the detector found, before anything is spent.
 *
 * The screen's whole job is to make doubt legible (design of record §5): say how the
 * page numbers were mapped and which pages proved it; label the pages that belong to
 * no chapter; give every uncertain boundary its reason and a way to look at the page;
 * let the student edit, merge, split, add and delete; and put pages, quota and time
 * next to the one button that spends anything.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { Estimate, RevisePlan } from "../api/api";
import { chapterSpan } from "../papers/chapters";
import { Meter } from "../billing/Meter";
import type { DetectedChapter, Gap, OffsetEvidence, OffsetSample, SourceDocument } from "./types";
import {
  addChapter,
  largestUnassignedRun,
  mergeWithNext,
  removeChapter,
  splitAt,
  toggleIncluded,
  visibleGaps,
} from "./edit";
import { blocking, coverage, estimateIngest, validate } from "./validate";
import { NOTHING_ANSWERED, answer, questionsFor, type Answered } from "./questions";
import { QuestionCards } from "./QuestionCards";
import type { Issue } from "./validate";
import "../styles/review.css";
import { Thumb, type Previewer } from "./Thumb";

export type Quota = Estimate["quota"];
/** A page's preview image url, or null when there is none. */
export type { Previewer } from "./Thumb";

/** A run of this many clean chapters folds into one "no problems found" row. */
export const FOLD_AT = 5;

function minutes(seconds: number): string {
  if (seconds < 90) return `${seconds} seconds`;
  return `${Math.round(seconds / 60)} minutes`;
}

/** Nothing found (design v2 § 9): say so, and offer the two ways on — add one by hand, or
 * read the contents page first. */
function NoChapters({
  document: doc,
  onAdd,
  onJump,
}: {
  document: SourceDocument;
  onAdd: () => void;
  onJump?: (page: number) => Promise<string | null>;
}) {
  const [text, setText] = useState<string | null>(null);
  const contents = doc.evidence?.contentsPages?.[0];
  return (
    <section className="review__none" aria-labelledby="review-none">
      <h2 id="review-none" className="review__none-title">
        We couldn’t find any chapters
      </h2>
      <p className="review__none-text">
        We could not find any chapters in this book. Add them by hand below — the contents page
        usually has everything you need.
      </p>
      <div className="review__none-actions">
        <button type="button" className="button button--primary" onClick={onAdd}>
          Add chapter
        </button>
        {contents !== undefined && onJump && (
          <button
            type="button"
            className="button"
            onClick={() => void onJump(contents).then((t) => setText(t))}
          >
            Check page {contents}
          </button>
        )}
      </div>
      {text && (
        <p className="chapter__excerpt review__none-excerpt">
          Page {contents} · “{text}”
        </p>
      )}
    </section>
  );
}

/** How the chapters were found, in plain words (bug fix 1, spec 6 §1): no offsets, no PDFs
 * named as such; the doubts themselves are the questions above the list. */
export function describeEvidence(evidence: OffsetEvidence | null | undefined): string | null {
  if (!evidence) return null;
  switch (evidence.method) {
    case "outline":
      return "We used the chapter bookmarks saved in your book's file.";
    case "headings":
      return "Your book has no bookmarks or readable contents page, so we found the chapters by their headings.";
    case "none":
      return "We could not find any chapters in this book. Add them by hand below — the contents page usually has everything you need.";
    case "contents": {
      if (evidence.offset === null)
        return "We read your book's contents page, but couldn't match its page numbers to the pages of the file, so we assumed they are the same.";
      return `We read your book's contents page and matched its page numbers to the file. Printed page 1 is page ${1 + evidence.offset} of the file.`;
    }
  }
}

function sampleCaption(s: OffsetSample): string {
  return `pdf ${s.pdfPage} → ${s.printed ?? "?"}${s.agrees ? " ✓" : ""}`;
}

function ChapterRow({
  chapter,
  issues,
  isLast,
  onChange,
  onMerge,
  onSplit,
  onRemove,
  onToggle,
  onJump,
  onPreview,
  asked,
  marker,
}: {
  chapter: DetectedChapter;
  issues: Issue[];
  /** Fixing a ready book: what saving would do to this chapter. */
  marker?: string;
  /** The id of this chapter's open question above, if it has one. */
  asked?: string;
  isLast: boolean;
  onChange: (next: DetectedChapter) => void;
  onMerge: () => void;
  onSplit: (page: number) => void;
  onRemove: () => void;
  onToggle: () => void;
  onJump?: (page: number) => Promise<string | null>;
  onPreview?: Previewer;
}) {
  const hasError = issues.some((issue) => issue.level === "error");
  const hasWarning = issues.some((issue) => issue.level === "warning");
  const skipped = chapter.included === false;
  const [splitPage, setSplitPage] = useState("");
  const [more, setMore] = useState(false);
  const moreRef = useRef<HTMLSpanElement>(null);
  // The menu closes on Escape and on a press outside it, as a menu does.
  useEffect(() => {
    if (!more) return;
    const away = (e: Event) => {
      if (!moreRef.current?.contains(e.target as Node)) setMore(false);
    };
    const key = (e: KeyboardEvent) => e.key === "Escape" && setMore(false);
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", key);
    };
  }, [more]);
  // An action ends the menu: the row it belonged to may now be a different chapter.
  const then = (act: () => void) => () => {
    setMore(false);
    act();
  };
  // A flagged chapter whose start page was read opens on its excerpt: the reason and the
  // page's own words side by side are the evidence.
  const [preview, setPreview] = useState<string | null | undefined>(
    hasWarning && chapter.excerpt ? chapter.excerpt : undefined,
  );
  const [previewPage, setPreviewPage] = useState(chapter.startPage);
  const bad = new Set(issues.filter((i) => i.level === "error").map((i) => i.field));
  const modifier =
    (hasError ? " chapter--invalid" : hasWarning ? " chapter--uncertain" : "") +
    (skipped ? " chapter--skipped" : "");

  return (
    <li className={`chapter${modifier}`} aria-label={`Chapter ${chapter.number}`}>
      <span className="chapter__number">{chapter.number}</span>
      {marker && (
        <span
          className={`chapter__marker${
            marker === "Will be made again" ? " chapter__marker--changed" : ""
          }`}
        >
          {marker}
        </span>
      )}

      <span className="chapter__title">
        <input
          className="chapter__input"
          value={chapter.title}
          aria-label={`Title of chapter ${chapter.number}`}
          onChange={(event) => onChange({ ...chapter, title: event.target.value })}
        />
      </span>

      <span className="chapter__pages">
        <input
          className="chapter__page-input"
          type="number"
          value={chapter.startPage}
          aria-label={`First page of chapter ${chapter.number}`}
          aria-invalid={bad.has("start") || undefined}
          onChange={(event) => onChange({ ...chapter, startPage: Number(event.target.value) })}
        />
        <span aria-hidden="true">–</span>
        <input
          className="chapter__page-input"
          type="number"
          value={chapter.endPage}
          aria-label={`Last page of chapter ${chapter.number}`}
          aria-invalid={bad.has("end") || undefined}
          onChange={(event) => onChange({ ...chapter, endPage: Number(event.target.value) })}
        />
      </span>

      <span className="chapter__actions">
        {onJump && !skipped && (
          <button
            type="button"
            className="chapter__link"
            onClick={() => {
              if (preview !== undefined) return setPreview(undefined);
              setPreviewPage(chapter.startPage);
              void onJump(chapter.startPage).then((text) => setPreview(text));
            }}
          >
            {preview === undefined ? `See page ${chapter.startPage}` : "Hide preview"}
          </button>
        )}
        {asked && !skipped && (
          <a className="chapter__link chapter__question" href={`#question-${asked}`}>
            Question above
          </a>
        )}
        {!skipped && (
          <button type="button" className="chapter__link chapter__link--quiet" onClick={onToggle}>
            Leave out
          </button>
        )}
        <span className="chapter__more" ref={moreRef}>
          <button
            type="button"
            className="chapter__link chapter__link--quiet"
            aria-expanded={more}
            onClick={() => setMore((m) => !m)}
          >
            More ▾
          </button>
          {more && (
            <span
              className="chapter__menu"
              role="group"
              aria-label={`More for chapter ${chapter.number}`}
            >
              {!isLast && (
                <button type="button" className="chapter__menu-item" onClick={then(onMerge)}>
                  Merge with next
                </button>
              )}
              <span className="chapter__split">
                <input
                  className="chapter__page-input"
                  type="number"
                  placeholder="page"
                  value={splitPage}
                  aria-label={`Split chapter ${chapter.number} at page`}
                  onChange={(event) => setSplitPage(event.target.value)}
                />
                <button
                  type="button"
                  className="chapter__menu-item"
                  disabled={splitPage === ""}
                  onClick={then(() => {
                    onSplit(Number(splitPage));
                    setSplitPage("");
                  })}
                >
                  Split
                </button>
              </span>
              <button
                type="button"
                className="chapter__menu-item chapter__menu-item--danger"
                onClick={then(onRemove)}
              >
                Delete
              </button>
            </span>
          )}
        </span>
      </span>

      {issues.length > 0 && !skipped && (
        <span className="chapter__notes">
          {issues.map((issue) => (
            <span
              key={issue.message}
              className={
                issue.level === "error" ? "chapter__note chapter__note--error" : "chapter__note"
              }
            >
              {issue.message}
              {issue.fix && (
                <button
                  type="button"
                  className="chapter__fix"
                  onClick={() =>
                    onChange({
                      ...chapter,
                      startPage: issue.fix?.startPage ?? chapter.startPage,
                      endPage: issue.fix?.endPage ?? chapter.endPage,
                    })
                  }
                >
                  {issue.fix.label}
                </button>
              )}
            </span>
          ))}
        </span>
      )}
      {skipped && (
        <span className="chapter__skipped">
          Left out — no flashcards, and it can’t be in a paper. It doesn’t use your pages. You can
          add it later from the subject page.{" "}
          <button type="button" className="chapter__link" onClick={onToggle}>
            Put it back
          </button>
        </span>
      )}

      {preview !== undefined && (
        <span className="chapter__evidence">
          {onPreview && (
            <Thumb page={previewPage} onPreview={onPreview} caption={`pdf ${previewPage}`} />
          )}
          <blockquote className="chapter__excerpt">
            {preview === null
              ? "No preview is available for this page."
              : `Page ${previewPage} · “${preview}”`}
          </blockquote>
        </span>
      )}
    </li>
  );
}

function GapRow({ gap }: { gap: Gap }) {
  const pages =
    gap.startPage === gap.endPage
      ? `Page ${gap.startPage}`
      : `Pages ${gap.startPage}–${gap.endPage}`;
  return (
    <li className="gap" aria-label={`Gap ${gap.startPage}-${gap.endPage}`}>
      {pages} · {gap.label} — no flashcards
    </li>
  );
}

type Item =
  | { kind: "chapter"; at: number; chapter: DetectedChapter }
  | { kind: "gap"; at: number; gap: Gap }
  | { kind: "fold"; at: number; chapters: DetectedChapter[] };

/** Runs of FOLD_AT or more clean chapters become one row; everything else stays. */
function fold(items: Item[], clean: (c: DetectedChapter) => boolean): Item[] {
  const out: Item[] = [];
  let run: DetectedChapter[] = [];
  const flush = () => {
    if (run.length >= FOLD_AT) out.push({ kind: "fold", at: run[0]!.startPage, chapters: run });
    else for (const c of run) out.push({ kind: "chapter", at: c.startPage, chapter: c });
    run = [];
  };
  for (const item of items) {
    if (item.kind === "chapter" && clean(item.chapter)) run.push(item.chapter);
    else {
      flush();
      out.push(item);
    }
  }
  flush();
  return out;
}

export function ChapterReview({
  document: initial,
  onIngest,
  quota,
  meter = null,
  onJump,
  onPreview,
  busy = false,
  error = null,
  mode = "review",
  onPlan,
  onCancel,
  focusChapter,
}: {
  /** "fix": a ready book's chapters changed after its flashcards were made (spec 7). */
  mode?: "review" | "fix";
  /** Fixing: the server's plan for the list as it stands (what is kept, remade, charged). */
  onPlan?: (doc: SourceDocument) => Promise<RevisePlan | null>;
  onCancel?: () => void;
  /** Fixing from study's "Wrong pages?": the chapter to bring into view. */
  focusChapter?: number;
  document: SourceDocument;
  onIngest?: (doc: SourceDocument) => void;
  quota?: Quota | null;
  /** The pages meter this month, for the commit bar's hold. */
  meter?: { used: number; limit: number } | null;
  onJump?: (page: number) => Promise<string | null>;
  onPreview?: Previewer;
  busy?: boolean;
  error?: string | null;
}) {
  const [doc, setDoc] = useState(initial);
  const fixing = mode === "fix";
  const [shown, setShown] = useState<Set<number>>(() => new Set());
  const [answered, setAnswered] = useState<Answered>(NOTHING_ANSWERED);
  const questions = useMemo(() => questionsFor(doc, answered), [doc, answered]);
  // Once there were questions, the block stays, to say they are all answered.
  const [asked] = useState(() => questionsFor(initial, NOTHING_ANSWERED).length > 0);
  const askedOf = new Map<number, string>();
  for (const q of questions) {
    if (q.kind === "start" || q.kind === "title") askedOf.set(q.chapter, q.id);
    if (q.kind === "book") for (const c of q.samples) askedOf.set(c.number, q.id);
  }
  const open = fixing ? 0 : questions.length;

  // Fixing: what each chapter becomes, by page range against the flashcards already made.
  const made = useMemo(
    () =>
      new Map(
        initial.chapters
          .filter((c) => c.ingested && c.included !== false)
          .map((c) => [`${c.startPage}-${c.endPage}`, c]),
      ),
    [initial],
  );
  const markerOf = (c: DetectedChapter): string | undefined => {
    if (!fixing) return undefined;
    if (c.included === false) return "Left out";
    const was = made.get(`${c.startPage}-${c.endPage}`);
    if (!was) return "Will be made again";
    return was.title.trim() === c.title.trim() ? "Made" : "New name only";
  };
  const shape = (d: SourceDocument) =>
    JSON.stringify(
      [...d.chapters]
        .sort((a, b) => a.startPage - b.startPage)
        .map((c) => [c.title.trim(), c.startPage, c.endPage, c.included !== false]),
    );
  const changed = fixing && shape(doc) !== shape(initial);
  const [plan, setPlan] = useState<RevisePlan | null>(null);
  useEffect(() => {
    if (!fixing || !changed || !onPlan) return setPlan(null);
    let live = true;
    setPlan(null);
    // Asked again as the student edits, a moment after they stop.
    const t = setTimeout(() => void onPlan(doc).then((p) => live && setPlan(p)), 350);
    return () => {
      live = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, fixing, changed]);
  useEffect(() => {
    if (focusChapter === undefined) return;
    document
      .querySelector(`[aria-label="Chapter ${focusChapter}"]`)
      ?.scrollIntoView?.({ block: "center" });
  }, [focusChapter]);

  const issues = useMemo(() => validate(doc), [doc]);
  const errors = useMemo(() => blocking(issues), [issues]);
  const pages = useMemo(() => coverage(doc), [doc]);
  const estimate = useMemo(() => estimateIngest(doc), [doc]);
  const gaps = useMemo(() => visibleGaps(doc), [doc]);
  const evidence = describeEvidence(doc.evidence);

  function update(next: DetectedChapter) {
    setDoc((current) => ({
      ...current,
      chapters: current.chapters.map((c) => (c.number === next.number ? next : c)),
    }));
  }

  function add() {
    const run = largestUnassignedRun(doc) ?? { startPage: doc.pageCount, endPage: doc.pageCount };
    setDoc(addChapter(doc, { title: "New chapter", ...run }));
  }

  // Chapters and labelled gaps interleave in page order; long clean runs fold.
  const items = fold(
    [
      ...doc.chapters.map((chapter): Item => ({ kind: "chapter", at: chapter.startPage, chapter })),
      ...gaps.map((gap): Item => ({ kind: "gap", at: gap.startPage, gap })),
    ].sort((a, b) => a.at - b.at),
    (c) =>
      !fixing &&
      c.included !== false &&
      !shown.has(c.number) &&
      !askedOf.has(c.number) &&
      !issues.some((issue) => issue.chapter === c.number),
  );
  const last = doc.chapters.reduce((m, c) => Math.max(m, c.number), 0);
  // Live against the edited list, so a skip re-enables the button at once; the server
  // decides again on Start. Without a number to compare, the server's word stands.
  const left = quota?.remaining ?? null;
  const overQuota = quota ? (left !== null ? estimate.pages > left : !quota.allowed) : false;
  const samples = doc.evidence?.samples ?? [];

  return (
    <main className="review">
      <header className="review__header">
        <div className="review__file">
          {doc.filename} · {doc.pageCount} pages
        </div>
        <h1 className="review__title">
          {fixing ? "Fix the chapters" : "Check the chapters before we start"}
        </h1>
        <p className="review__lede">
          {fixing ? (
            <>
              Change any chapter’s pages or name. Only the chapters you change are made again — the
              rest keep their flashcards.
            </>
          ) : asked ? (
            <>
              We found <strong>{doc.chapters.length} chapters</strong>. Each one becomes a set of
              flashcards, made from its pages. Before we start, answer the questions below — they’re
              the places we weren’t sure. Everything else looks right.
            </>
          ) : (
            <>
              We found <strong>{doc.chapters.length} chapters</strong> and we’re confident about all
              of them. Look over the list if you like, then make your flashcards.
            </>
          )}
        </p>
        <div className="review__stats">
          <span>
            <strong>{doc.chapters.length}</strong> chapters
          </span>
          <span>
            <strong>{`${pages.assigned} of ${pages.total}`}</strong> pages covered
          </span>
          {pages.unassigned > 0 && (
            <span>
              <strong>{pages.unassigned}</strong> pages in no chapter
            </span>
          )}
        </div>
        {evidence && (
          <details className="review__evidence">
            <summary>How we found these</summary>
            <div className="review__evidence-text">
              <p>{evidence}</p>
            </div>
            {onPreview && samples.length > 0 && (
              <div className="review__thumbs">
                {samples.map((s) => (
                  <Thumb
                    key={s.pdfPage}
                    page={s.pdfPage}
                    onPreview={onPreview}
                    caption={sampleCaption(s)}
                    warn={!s.agrees}
                  />
                ))}
              </div>
            )}
          </details>
        )}
      </header>

      {!fixing && (
        <QuestionCards
          questions={questions}
          chapters={new Map(doc.chapters.map((c) => [c.number, c]))}
          asked={asked}
          onAnswer={(q, reply) => {
            const next = answer(doc, answered, q, reply);
            setDoc(next.doc);
            setAnswered(next.answered);
          }}
          onTitle={(n, title) =>
            setDoc((d) => ({
              ...d,
              chapters: d.chapters.map((c) => (c.number === n ? { ...c, title } : c)),
            }))
          }
          onJump={onJump}
          onPreview={onPreview}
        />
      )}

      <ul className="chapters">
        {items.map((item) => {
          if (item.kind === "gap") return <GapRow key={`g${item.gap.startPage}`} gap={item.gap} />;
          if (item.kind === "fold") {
            const first = item.chapters[0]!;
            const end = item.chapters[item.chapters.length - 1]!;
            return (
              <li key={`f${first.number}`} className="fold">
                Chapters {first.number}–{end.number} · pages {first.startPage}–{end.endPage} · no
                problems found{" "}
                <button
                  type="button"
                  className="chapter__link"
                  aria-label={`Show chapters ${first.number} to ${end.number}`}
                  onClick={() =>
                    setShown((s) => new Set([...s, ...item.chapters.map((c) => c.number)]))
                  }
                >
                  Show
                </button>
              </li>
            );
          }
          const chapter = item.chapter;
          return (
            <ChapterRow
              key={`c${chapter.number}`}
              chapter={chapter}
              issues={issues.filter((issue) => issue.chapter === chapter.number)}
              isLast={chapter.number === last}
              onChange={update}
              onMerge={() => setDoc(mergeWithNext(doc, chapter.number))}
              onSplit={(page) => setDoc(splitAt(doc, chapter.number, page))}
              onRemove={() => setDoc(removeChapter(doc, chapter.number))}
              onToggle={() => setDoc(toggleIncluded(doc, chapter.number))}
              onJump={onJump}
              onPreview={onPreview}
              asked={fixing ? undefined : askedOf.get(chapter.number)}
              marker={markerOf(chapter)}
            />
          );
        })}
      </ul>

      {doc.chapters.length === 0 ? (
        <NoChapters document={doc} onAdd={add} onJump={onJump} />
      ) : (
        <div className="review__add">
          <button type="button" className="button" onClick={add}>
            Add chapter
          </button>
        </div>
      )}

      {fixing ? (
        <FixCommit
          errors={errors.length}
          changed={changed}
          plan={plan}
          busy={busy}
          error={error}
          onSave={() => onIngest?.(doc)}
          onCancel={onCancel}
        />
      ) : (
        <div className="review__commit">
          <div className="review__estimate">
            {errors.length > 0 ? (
              <>
                <strong className="review__problems">
                  {errors.length} problem{errors.length === 1 ? "" : "s"} to fix
                </strong>{" "}
                before the flashcards can be made. {errors.length === 1 ? "It is" : "They are"}{" "}
                marked above.
              </>
            ) : overQuota ? (
              <>
                <strong>
                  Over your allowance
                  {left !== null ? ` by ${estimate.pages - left} pages` : ""}.
                </strong>{" "}
                Making flashcards from these chapters needs {estimate.pages} pages
                {left !== null ? ` and you have ${left} pages left` : ""}. Leave some chapters out
                for now — you can add them later — or wait for your allowance to renew.
              </>
            ) : (
              <>
                {open > 0 && (
                  <span className="review__unanswered">
                    <strong>
                      {open} question{open === 1 ? "" : "s"} not answered.
                    </strong>{" "}
                    If a chapter starts on the wrong page, its flashcards will include pages from
                    the chapter next to it. You can fix a chapter’s pages later from the subject
                    page; only that chapter is made again.{" "}
                  </span>
                )}
                Making flashcards from{" "}
                <strong>
                  {estimate.skipped > 0
                    ? `${estimate.chapters} of ${estimate.chapters + estimate.skipped} chapters`
                    : `${estimate.chapters} chapters`}
                </strong>
                {estimate.skipped > 0 &&
                  ` (${estimate.skipped} left out — add them any time from the subject page)`}
                . About <strong>{minutes(estimate.seconds)}</strong>, and it uses{" "}
                <strong>{estimate.pages} pages</strong> of your allowance
                {left !== null ? ` (${left - estimate.pages} left)` : ""}.
              </>
            )}
            {meter && meter.limit > 0 && (
              <div className="review__meter">
                <Meter
                  label="Pages this month"
                  meter={{
                    used: meter.used,
                    held: errors.length > 0 ? 0 : estimate.pages,
                    limit: meter.limit,
                  }}
                />
              </div>
            )}
            {error && (
              <div className="review__error" role="alert">
                {error}
              </div>
            )}
          </div>

          <button
            type="button"
            className="button button--primary"
            disabled={errors.length > 0 || overQuota || busy}
            onClick={() => onIngest?.(doc)}
          >
            {busy ? "Starting…" : "Make flashcards"}
          </button>
        </div>
      )}
    </main>
  );
}

/** "Making 2 chapters again (chapters 6 and 7) …": the fix's commit bar, from the server's
 * plan, which alone knows which pages were counted already (bug fix 1, spec 7). */
function FixCommit({
  errors,
  changed,
  plan,
  busy,
  error,
  onSave,
  onCancel,
}: {
  errors: number;
  changed: boolean;
  plan: RevisePlan | null;
  busy: boolean;
  error: string | null;
  onSave: () => void;
  onCancel?: () => void;
}) {
  const names = plan !== null && plan.remake.length === 0;
  const left = plan?.quota?.remaining ?? null;
  const over = plan?.quota ? !plan.quota.allowed : false;
  const n = plan?.remake.length ?? 0;
  return (
    <div className="review__commit">
      <div className="review__estimate" aria-live="polite">
        {errors > 0 ? (
          <>
            <strong className="review__problems">
              {errors} problem{errors === 1 ? "" : "s"} to fix
            </strong>{" "}
            before saving. {errors === 1 ? "It is" : "They are"} marked above.
          </>
        ) : !changed ? (
          "Change a chapter’s pages or name above. Only the chapters you change are made again."
        ) : plan === null ? (
          "Working out what this changes…"
        ) : names ? (
          "New names only — nothing is made again, and it doesn’t use any of your allowance."
        ) : over ? (
          <>
            <strong>Over your allowance.</strong> This needs {plan.billable_pages} new pages
            {left !== null ? ` and you have ${left} left` : ""}.
          </>
        ) : (
          <>
            Making{" "}
            <strong>
              {n} chapter{n === 1 ? "" : "s"}
            </strong>{" "}
            again ({n === 1 ? "chapter" : "chapters"} {chapterSpan(plan.remake)}). About{" "}
            <strong>{minutes(plan.seconds ?? 40)}</strong>.{" "}
            {plan.billable_pages > 0 ? (
              <>
                It uses <strong>{plan.billable_pages} pages</strong> of your allowance — the rest of
                these pages were already counted.
                {left !== null ? ` (${left - plan.billable_pages} left)` : ""}
              </>
            ) : (
              "It doesn’t use any of your allowance."
            )}
          </>
        )}
        {error && (
          <div className="review__error" role="alert">
            {error}
          </div>
        )}
      </div>
      <div className="review__commit-actions">
        {onCancel && (
          <button type="button" className="button" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button
          type="button"
          className="button button--primary"
          disabled={errors > 0 || !changed || plan === null || over || busy}
          onClick={onSave}
        >
          {busy ? "Saving…" : names ? "Save names" : "Make these chapters again"}
        </button>
      </div>
    </div>
  );
}
